/**
 * AdminTravelToolsGuideCards — admin CRUD for the user-facing "📖 Guide" tab content.
 *
 * Different from the existing "📖 Guide" admin tab (which manages the ? help modal's
 * "How It Works" content). This component manages the CARDS shown on a new top-level
 * Travel Tools tab — Morocco travel content like Emergency / Tipping / SIM / Cultural.
 *
 * Backend endpoints (all multipart/form-data, all admin-gated):
 *   GET    /admin/guide-cards
 *   POST   /admin/guide-cards                (Form: title, icon, content)
 *   PUT    /admin/guide-cards/reorder        (Form: order = JSON array of ids)
 *   PUT    /admin/guide-cards/{id}           (Form: title, icon, content)
 *   DELETE /admin/guide-cards/{id}
 */
import React, { useEffect, useState } from 'react';

const AdminTravelToolsGuideCards = ({ API, authHeaders, onMessage }) => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [newCard, setNewCard] = useState({ title: '', icon: '📖', content: '' });
  const [editing, setEditing] = useState(null); // card object being edited
  const [expandedId, setExpandedId] = useState(null);

  const toast = (msg) => { if (typeof onMessage === 'function') onMessage(msg); };

  const formAuth = { ...(authHeaders || {}) };
  delete formAuth['Content-Type'];
  delete formAuth['content-type'];

  // Initial fetch
  useEffect(() => {
    let aborted = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const res = await fetch(`${API}/api/travel-tools/admin/guide-cards`, { headers: authHeaders });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!aborted) setCards(data.cards || []);
      } catch (e) {
        if (!aborted) setError(String(e.message || e));
      } finally {
        if (!aborted) setLoading(false);
      }
    })();
    return () => { aborted = true; };
  }, [API, authHeaders]);

  const refresh = async () => {
    try {
      const res = await fetch(`${API}/api/travel-tools/admin/guide-cards`, { headers: authHeaders });
      if (res.ok) {
        const data = await res.json();
        setCards(data.cards || []);
      }
    } catch {}
  };

  const addCard = async () => {
    if (!newCard.title.trim()) { toast('Title required'); return; }
    setBusy('add');
    try {
      const fd = new FormData();
      fd.append('title', newCard.title.trim());
      fd.append('icon', newCard.icon || '📖');
      fd.append('content', newCard.content || '');
      const res = await fetch(`${API}/api/travel-tools/admin/guide-cards`, {
        method: 'POST', headers: formAuth, body: fd
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setNewCard({ title: '', icon: '📖', content: '' });
      await refresh();
      toast('Card added');
    } catch (e) {
      toast(`Add failed: ${e.message || e}`);
    } finally { setBusy(null); }
  };

  const saveEdit = async () => {
    if (!editing || !editing.id) return;
    setBusy(`edit-${editing.id}`);
    try {
      const fd = new FormData();
      fd.append('title', editing.title || '');
      fd.append('icon', editing.icon || '📖');
      fd.append('content', editing.content || '');
      const res = await fetch(`${API}/api/travel-tools/admin/guide-cards/${editing.id}`, {
        method: 'PUT', headers: formAuth, body: fd
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setCards(prev => prev.map(c => c.id === editing.id ? { ...c, ...editing } : c));
      setEditing(null);
      toast('Card updated');
    } catch (e) {
      toast(`Save failed: ${e.message || e}`);
    } finally { setBusy(null); }
  };

  const deleteCard = async (card) => {
    if (!window.confirm(`Delete card "${card.title}"?`)) return;
    setBusy(`del-${card.id}`);
    try {
      const res = await fetch(`${API}/api/travel-tools/admin/guide-cards/${card.id}`, {
        method: 'DELETE', headers: authHeaders
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setCards(prev => prev.filter(c => c.id !== card.id));
      toast('Card deleted');
    } catch (e) {
      toast(`Delete failed: ${e.message || e}`);
    } finally { setBusy(null); }
  };

  const move = async (idx, direction) => {
    const target = idx + (direction === 'up' ? -1 : 1);
    if (target < 0 || target >= cards.length) return;
    const next = cards.slice();
    [next[target], next[idx]] = [next[idx], next[target]];
    const prev = cards;
    setCards(next); // optimistic
    try {
      const fd = new FormData();
      fd.append('order', JSON.stringify(next.map(c => c.id)));
      const res = await fetch(`${API}/api/travel-tools/admin/guide-cards/reorder`, {
        method: 'PUT', headers: formAuth, body: fd
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast(direction === 'up' ? 'Moved up' : 'Moved down');
    } catch (e) {
      setCards(prev); // rollback
      toast(`Reorder failed: ${e.message || e} — backend may need restart`);
    }
  };

  if (loading) {
    return <div className="bg-gray-800 rounded-xl p-12 text-center text-gray-400">Loading guide cards…</div>;
  }
  if (error) {
    return (
      <div className="bg-red-900/30 border border-red-700 rounded-xl p-6 text-red-200">
        <div className="font-bold mb-2">Could not load guide cards</div>
        <div className="text-sm">{error}</div>
        <div className="text-xs text-red-300 mt-2">If this is the first time using this tab, restart the backend so the new endpoints register.</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Intro */}
      <div className="bg-gradient-to-r from-teal-900/50 to-cyan-900/50 rounded-xl p-6 border border-teal-700/50">
        <div className="flex items-center gap-3 mb-2">
          <span className="text-4xl">📖</span>
          <div>
            <h2 className="text-2xl font-bold text-white">Guide Cards</h2>
            <p className="text-gray-300 text-sm">
              Content shown on the user-facing <strong>📖 Guide</strong> tab in Travel Tools.
              Add cards for Emergency phrases, Tipping &amp; Haggling, Local SIM, Cultural tips — anything Morocco-specific that travelers should know.
            </p>
            <p className="text-gray-400 text-xs mt-1">Different from the ? help modal ("How It Works") which is managed in the other 📖 Guide tab.</p>
          </div>
        </div>
      </div>

      {/* Add new card */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 space-y-3">
        <div className="text-lg font-bold flex items-center gap-2">
          <span>➕</span> Add new card
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={newCard.icon}
            onChange={(e) => setNewCard({ ...newCard, icon: e.target.value })}
            placeholder="📖"
            className="w-16 px-2 py-2 bg-gray-700 border border-gray-600 rounded-lg text-center text-xl text-white"
          />
          <input
            type="text"
            value={newCard.title}
            onChange={(e) => setNewCard({ ...newCard, title: e.target.value })}
            placeholder="Card title (e.g. Emergency numbers)"
            className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
          />
        </div>
        <textarea
          value={newCard.content}
          onChange={(e) => setNewCard({ ...newCard, content: e.target.value })}
          placeholder={'Card content. Plain text. Use line breaks for paragraphs.\n\nExample:\nPolice: 19\nAmbulance: 15\n\nUseful phrase:\n"Ana mrid" — I am sick'}
          rows={8}
          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm font-mono"
        />
        <button
          onClick={addCard}
          disabled={busy === 'add' || !newCard.title.trim()}
          className="w-full px-4 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-bold"
        >
          {busy === 'add' ? 'Adding…' : '+ Add Card'}
        </button>
      </div>

      {/* Cards list */}
      <div className="space-y-2">
        <div className="text-sm font-bold text-gray-400 uppercase tracking-wider px-1">
          Cards ({cards.length})
        </div>
        {cards.length === 0 ? (
          <div className="bg-gray-800 rounded-xl p-8 text-center text-gray-500 text-sm">
            No cards yet. Add one above to get started.
          </div>
        ) : (
          cards.map((card, idx) => (
            <div key={card.id} className="bg-gray-700 rounded-lg overflow-hidden">
              {editing?.id === card.id ? (
                <div className="p-4 space-y-3">
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={editing.icon || ''}
                      onChange={(e) => setEditing({ ...editing, icon: e.target.value })}
                      className="w-14 px-2 py-2 bg-gray-600 rounded text-center text-xl text-white"
                    />
                    <input
                      type="text"
                      value={editing.title || ''}
                      onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                      className="flex-1 px-3 py-2 bg-gray-600 rounded text-white"
                      placeholder="Card title"
                    />
                  </div>
                  <textarea
                    value={editing.content || ''}
                    onChange={(e) => setEditing({ ...editing, content: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-600 rounded h-64 resize-y text-sm font-mono text-white"
                    placeholder="Card content..."
                  />
                  <div className="flex gap-2 justify-end">
                    <button
                      onClick={() => setEditing(null)}
                      className="px-4 py-2 bg-gray-600 hover:bg-gray-500 rounded text-sm text-white"
                    >
                      ✕ Cancel
                    </button>
                    <button
                      onClick={saveEdit}
                      disabled={busy === `edit-${card.id}`}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 rounded text-sm font-bold text-white"
                    >
                      {busy === `edit-${card.id}` ? 'Saving…' : '✓ Save Changes'}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div
                    className="flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-650"
                    onClick={() => setExpandedId(expandedId === card.id ? null : card.id)}
                  >
                    <span className="text-2xl w-10 text-center">{card.icon}</span>
                    <span className="flex-1 font-medium text-white">{card.title}</span>
                    <span className="text-gray-500 text-sm">#{idx + 1}</span>
                    <span className="text-gray-400 text-sm">
                      {expandedId === card.id ? '▼' : '▶'}
                    </span>
                    <button
                      onClick={(e) => { e.stopPropagation(); move(idx, 'up'); }}
                      disabled={idx === 0}
                      title="Move up"
                      className="px-2 py-1 bg-gray-600 hover:bg-gray-500 disabled:opacity-30 disabled:cursor-not-allowed rounded text-xs text-white"
                    >
                      ▲
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); move(idx, 'down'); }}
                      disabled={idx === cards.length - 1}
                      title="Move down"
                      className="px-2 py-1 bg-gray-600 hover:bg-gray-500 disabled:opacity-30 disabled:cursor-not-allowed rounded text-xs text-white"
                    >
                      ▼
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setEditing({ ...card }); }}
                      className="px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded text-sm text-white"
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteCard(card); }}
                      disabled={busy === `del-${card.id}`}
                      className="px-3 py-1 bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded text-sm text-white"
                    >
                      🗑️
                    </button>
                  </div>
                  {expandedId === card.id && card.content && (
                    <div className="px-4 pb-4 pt-1 border-t border-gray-600">
                      <pre className="text-sm text-gray-300 whitespace-pre-wrap font-sans">{card.content}</pre>
                    </div>
                  )}
                </>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminTravelToolsGuideCards;
